import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, Mail, MapPin, Phone } from "lucide-react";
import { getSiteConfig } from "@/lib/api";
import { IMAGES } from "@/lib/constants";
import { Reveal } from "@/components/Reveal";

export default function Contact() {
  const [config, setConfig] = useState(null);
  useEffect(() => {
    getSiteConfig().then(setConfig);
  }, []);

  return (
    <main className="pt-32 pb-24 px-6 lg:px-12 max-w-7xl mx-auto min-h-screen">
      <Reveal>
        <p className="eyebrow">Contact</p>
        <h1 className="mt-4 font-serif font-light text-4xl sm:text-5xl text-ink tracking-tight text-balance">
          The studio door is open.
        </h1>
        <p className="mt-6 max-w-xl text-base text-ink-muted leading-relaxed">
          Write, call or visit. Every conversation starts with your occasion —
          and ends with something worth keeping.
        </p>
      </Reveal>

      <div className="mt-14 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 space-y-5">
          <Reveal>
            <a
              data-testid="contact-email-link"
              href={`mailto:${config?.contact_email || "hello@gratiora.in"}`}
              className="group flex items-center gap-6 border border-gold/15 bg-white p-7 hover:border-gold/40 transition-colors duration-400"
            >
              <Mail size={22} strokeWidth={1.5} className="text-gold-brass shrink-0" />
              <div>
                <p className="eyebrow !text-[10px]">Email</p>
                <p className="mt-1 font-serif text-xl text-ink group-hover:text-gold-brass transition-colors duration-300">
                  {config?.contact_email || "hello@gratiora.in"}
                </p>
              </div>
            </a>
          </Reveal>
          <Reveal delay={0.08}>
            <a
              data-testid="contact-phone-link"
              href={`tel:+${config?.whatsapp_number || "919818613182"}`}
              className="group flex items-center gap-6 border border-gold/15 bg-white p-7 hover:border-gold/40 transition-colors duration-400"
            >
              <Phone size={22} strokeWidth={1.5} className="text-gold-brass shrink-0" />
              <div>
                <p className="eyebrow !text-[10px]">Phone</p>
                <p className="mt-1 font-serif text-xl text-ink group-hover:text-gold-brass transition-colors duration-300">
                  {config?.contact_phone || "+91 98186 13182"}
                </p>
              </div>
            </a>
          </Reveal>
          <Reveal delay={0.16}>
            <div data-testid="contact-studio-address" className="flex items-center gap-6 border border-gold/15 bg-white p-7">
              <MapPin size={22} strokeWidth={1.5} className="text-gold-brass shrink-0" />
              <div>
                <p className="eyebrow !text-[10px]">Studio</p>
                <p className="mt-1 font-serif text-xl text-ink">
                  {config?.studio || "Studio 14, Shahpur Jat, New Delhi — Delhi NCR"}
                </p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.24}>
            <div className="flex items-center gap-6 border border-gold/15 bg-white p-7">
              <Clock size={22} strokeWidth={1.5} className="text-gold-brass shrink-0" />
              <div>
                <p className="eyebrow !text-[10px]">Studio hours</p>
                <p className="mt-1 font-serif text-xl text-ink">Mon – Sat · 10:00 – 19:00 IST</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.3}>
            <Link
              to="/enquire"
              className="mt-4 inline-flex items-center gap-2 bg-ink text-cream px-8 py-4 text-sm tracking-wide hover:bg-gold-brass transition-colors duration-400 group"
            >
              Request a Quote
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="lg:col-span-5">
          <div className="relative h-full min-h-[320px]">
            <div className="absolute -inset-3 border border-gold/25" />
            <img src={IMAGES.studio} alt="The Gratiora studio in Delhi NCR" loading="lazy" className="relative h-full w-full object-cover min-h-[320px]" />
          </div>
        </Reveal>
      </div>
    </main>
  );
}
